import { z } from 'zod';

/**
 * Configuration for activities — Zod-based publish validation, categories, and statuses.
 *
 * Merged into the app config via configHelpers.mergeConfig(),
 * available to components via useConfiguration().activity
 *
 * Example:
 *   const config = useConfiguration();
 *   const { activityPublishSchema, categories, statusConfig } = config.activity;
 *   const result = activityPublishSchema.safeParse(activity);
 */

// ── Zod helpers for Supabase count aggregation ──

/** Accepts a plain array OR a Supabase [{count: n}] aggregation. Must have > 0 items. */
const positiveCountArray = z.custom(val => {
  if (!Array.isArray(val) || val.length === 0) return false;
  // Supabase count aggregation: [{count: n}]
  if (val.length === 1 && typeof val[0]?.count === 'number') return val[0].count > 0;
  return true;
});

// ── Publish schema ──

export const activityPublishSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  category: z.string().trim().min(1, 'Category is required'),
  description: z.string().trim().min(1, 'Description is required'),
  location_address: z.string().trim().min(1, 'Location is required'),
  sessions: positiveCountArray.refine(() => true, { message: 'At least one session is required' }),
  images: z.array(z.any()).min(1, 'At least one photo is required'),
});

/**
 * Validate an activity for publishing. Returns error messages array (empty = valid).
 *
 * @param {Object} activity Full activity object.
 * @returns {string[]} Human-readable error strings.
 */
export const validateActivityForPublish = activity => {
  if (!activity) return ['Activity not found'];
  const result = activityPublishSchema.safeParse(activity);
  if (result.success) return [];
  return result.error.issues.map(i => i.message);
};

/**
 * Activity categories.
 */
export const categories = [
  { value: 'summer-camps', label: 'Summer Camps' },
  { value: 'sports-athletics', label: 'Sports & Athletics' },
  { value: 'arts-crafts', label: 'Arts & Crafts' },
  { value: 'music-performing-arts', label: 'Music & Performing Arts' },
  { value: 'stem-coding-robotics', label: 'STEM / Coding / Robotics' },
  { value: 'dance', label: 'Dance' },
  { value: 'martial-arts', label: 'Martial Arts' },
  { value: 'tutoring-academic', label: 'Tutoring & Academic' },
  { value: 'swimming-aquatics', label: 'Swimming / Aquatics' },
  { value: 'other', label: 'Other' },
];

/**
 * Activity status display configuration.
 */
export const statusConfig = {
  draft: { label: 'Draft', colorClass: 'bg-gray-100 text-gray-800' },
  pendingApproval: { label: 'Pending Approval', colorClass: 'bg-blue-100 text-blue-800' },
  published: { label: 'Published', colorClass: 'bg-green-100 text-green-800' },
  closed: { label: 'Closed', colorClass: 'bg-amber-100 text-amber-800' },
  archived: { label: 'Archived', colorClass: 'bg-red-100 text-red-800' },
};
